/**
 * Compact (~56px) rows for the team's sounds (Recent creations): a sound (cover with play / pause, title — with its
 * brand's logo when it has one —, one subtitle line, one ⋯ menu), a generation in progress (skeleton cover, progress
 * bar, %) and a failed generation (error + Retry / Dismiss). `TrackMenu` is the ⋯ every sound surface shares (rows,
 * cards, the collection table).
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Download, FolderMinus, FolderPlus, Loader2, MoreHorizontal, Pencil, Plus, RotateCcw, Share2, Tag, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/components/ui/utils';
import { audioStudioApi, type StudioTrack } from '@/api/audioStudioApi';
import { PlayButton, usePlayer } from './AudioPlayer';
import { BrandAvatar, useStudioBrands } from './brands';
import { GenreTile } from './CoverArt';
import { capitalize, studioPlayable, trackSubtitle } from './format';
import { audioKeys } from './queries';
import { RIGHTS } from './rights';
import type { TrackActions } from './useTrackActions';

const menuBtn = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#667085] hover:bg-[#f2f4f7] data-[state=open]:bg-[#f2f4f7]';

export function RenameInline({ track, onDone }: { track: StudioTrack; onDone: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(track.title);
  const save = useMutation({
    mutationFn: () => audioStudioApi.updateTrack(track.track_id, { title: value.trim() }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: audioKeys.all }); toast.success('Renamed'); onDone(); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <form className="flex min-w-0 items-center gap-1" onSubmit={(e) => { e.preventDefault(); if (value.trim() && value.trim() !== track.title) save.mutate(); else onDone(); }}>
      <input autoFocus value={value} onChange={(e) => setValue(e.target.value)} maxLength={120} aria-label="Sound title" className="min-w-0 flex-1 rounded-md border border-[#d0d5dd] px-2 py-1 text-[13px]" onKeyDown={(e) => { if (e.key === 'Escape') onDone(); }} />
      <button type="submit" aria-label="Save title" className="rounded-md p-1 text-[#027a48] hover:bg-[#ecfdf3]">{save.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Check className="h-4 w-4" aria-hidden />}</button>
      <button type="button" aria-label="Cancel rename" onClick={onDone} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]"><X className="h-4 w-4" aria-hidden /></button>
    </form>
  );
}

/** The brand's logo next to a sound's title, when the sound has one of the team's brands. */
export function TrackBrandMark({ track, size = 14 }: { track: StudioTrack; size?: number }) {
  const brand = useStudioBrands().brandOf(track.brand_id);
  if (!brand) return null;
  return <span title={brand.brandName} aria-label={`Brand: ${brand.brandName}`} role="img" className="inline-flex shrink-0"><BrandAvatar brand={brand} size={size} /></span>;
}

/** A sound's ⋯ menu. `trigger` overrides the default 32px button (cards use a round one on the artwork). */
export function TrackMenu({ track: t, actions, triggerClassName }: { track: StudioTrack; actions: TrackActions; triggerClassName?: string }) {
  const title = t.title || 'Untitled';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`More actions for ${title}`} className={triggerClassName ?? menuBtn}><MoreHorizontal className="h-4 w-4" aria-hidden /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[190px]">
        <DropdownMenuItem onSelect={() => actions.attach(t)}><Plus className="h-4 w-4" aria-hidden /> Add to beat / brief</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => actions.addToCollection(t)}><FolderPlus className="h-4 w-4" aria-hidden /> Add to collection…</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => actions.share(t)}><Share2 className="h-4 w-4" aria-hidden /> Share</DropdownMenuItem>
        {t.is_owner && <DropdownMenuItem onSelect={() => actions.startRename(t)}><Pencil className="h-4 w-4" aria-hidden /> Rename</DropdownMenuItem>}
        {t.is_owner && actions.setBrand && <DropdownMenuItem onSelect={() => actions.setBrand?.(t)}><Tag className="h-4 w-4" aria-hidden /> Set brand…</DropdownMenuItem>}
        <DropdownMenuItem disabled={!t.audio_url} onSelect={() => actions.download(t)}><Download className="h-4 w-4" aria-hidden /> Download</DropdownMenuItem>
        {actions.removeFromCollection && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => actions.removeFromCollection?.(t)}><FolderMinus className="h-4 w-4" aria-hidden /> Remove from collection</DropdownMenuItem>
          </>
        )}
        {t.is_owner && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => actions.askDelete(t)} className="text-[#b42318] focus:text-[#b42318]"><Trash2 className="h-4 w-4" aria-hidden /> Delete</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SoundRow({ track: t, actions, showOwner = false, isNew = false, flash = false, now }: {
  track: StudioTrack;
  actions: TrackActions;
  /** Team view: name the maker when it isn't you. */
  showOwner?: boolean;
  /** Just finished — shows a "New" pill. */
  isNew?: boolean;
  /** Just finished — purple background that fades out. */
  flash?: boolean;
  now?: number;
}) {
  const { current } = usePlayer();
  const item = studioPlayable(t);
  const active = current?.id === item.id;
  const title = t.title || 'Untitled';
  const owner = showOwner && !t.is_owner ? t.owner_name || 'Team member' : null;
  const subtitle = trackSubtitle(t, { owner, now });
  const renaming = actions.renamingId === t.track_id;

  return (
    <li
      data-track-id={t.track_id}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'group/row flex h-14 items-center gap-3 px-3 transition-colors',
        flash ? 'bg-[#f4ebff] duration-0' : 'duration-[2000ms]',
        !flash && (active ? 'bg-[#f9fafb]' : 'hover:bg-[#fcfcfd]'),
      )}
    >
      <PlayButton item={item} size="row" coverUrl={t.cover_url} fallback={<GenreTile genre={capitalize(t.genre) || null} title={title} instrumental={t.music_type === 'Instrumental'} size="thumb" />} />
      <div className="min-w-0 flex-1">
        {renaming ? (
          <RenameInline track={t} onDone={actions.stopRename} />
        ) : (
          <>
            <div className="flex min-w-0 items-center gap-1.5">
              <TrackBrandMark track={t} />
              <span className={cn('truncate text-[14px] leading-5', active ? 'text-[#6941c6]' : 'text-[#101828]')} style={{ fontWeight: 600 }} title={`${title}\n${RIGHTS.original.tooltip}`}>{title}</span>
              {isNew && <span className="shrink-0 rounded-full bg-[#f4ebff] px-1.5 py-px text-[10px] leading-4 text-[#6941c6]" style={{ fontWeight: 600 }}>New</span>}
            </div>
            <p className="truncate text-[12.5px] leading-[18px] text-[#667085]" title={subtitle}>{subtitle}</p>
          </>
        )}
      </div>
      <TrackMenu track={t} actions={actions} />
    </li>
  );
}

/** A generation still being made. `progress` is 0–1. */
export function JobRow({ name, subtitle, progress }: { name: string; subtitle: string; progress: number }) {
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100);
  return (
    <li className="flex h-14 items-center gap-3 bg-[#fcfcfd] px-3" aria-busy="true">
      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#f2f4f7]">
        <span className="absolute inset-0 animate-pulse bg-[#eaecf0]" aria-hidden />
        <Loader2 className="relative h-4 w-4 animate-spin text-[#667085]" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] leading-5 text-[#101828]" style={{ fontWeight: 600 }} title={name}>{name}</div>
        <p className="truncate text-[12.5px] leading-[18px] text-[#667085]" title={subtitle}>{subtitle}</p>
        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-[#eaecf0]" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`${name} progress`}>
          <div className="h-full rounded-full bg-[#475467] transition-[width] duration-700" style={{ width: `${Math.max(pct, 4)}%` }} />
        </div>
      </div>
      <span className="w-8 shrink-0 text-right text-[11.5px] tabular-nums text-[#667085]">{pct}%</span>
    </li>
  );
}

/** A generation that failed — the error, plus Retry (when the request is known) and Dismiss in the ⋯ menu. */
export function FailedJobRow({ name, error, onRetry, retrying = false, onDismiss }: {
  name: string;
  error: string;
  onRetry?: () => void;
  retrying?: boolean;
  onDismiss: () => void;
}) {
  return (
    <li className="flex h-14 items-center gap-3 px-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#fef3f2]">
        {retrying ? <Loader2 className="h-4 w-4 animate-spin text-[#d92d20]" aria-hidden /> : <AlertCircle className="h-4 w-4 text-[#d92d20]" aria-hidden />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] leading-5 text-[#101828]" style={{ fontWeight: 600 }} title={name}>{name}</div>
        <p className="truncate text-[12.5px] leading-[18px] text-[#b42318]" title={error} role="alert">{error}</p>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label={`More actions for ${name}`} className={menuBtn}><MoreHorizontal className="h-4 w-4" aria-hidden /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onRetry && <DropdownMenuItem disabled={retrying} onSelect={onRetry}><RotateCcw className="h-4 w-4" aria-hidden /> Retry</DropdownMenuItem>}
          <DropdownMenuItem onSelect={onDismiss}><X className="h-4 w-4" aria-hidden /> Dismiss</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

export type SoundsScope = 'team' | 'mine';

/** Team / Mine segmented control (the Recent creations toolbar) — h-8, like the Trending period pills. */
export function ScopeToggle({ value, onChange, className }: { value: SoundsScope; onChange: (s: SoundsScope) => void; className?: string }) {
  return (
    <div role="radiogroup" aria-label="Whose sounds" className={cn('inline-flex h-8 shrink-0 items-center rounded-[8px] bg-[#f2f4f7] p-0.5', className)}>
      {(['team', 'mine'] as const).map((s) => (
        <button key={s} type="button" role="radio" aria-checked={value === s} onClick={() => onChange(s)} className={cn('h-7 rounded-[6px] px-3 text-[12.5px] leading-[18px] transition-colors', value === s ? 'bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.06)]' : 'text-[#667085] hover:text-[#344054]')} style={{ fontWeight: 500 }}>
          {s === 'team' ? 'Team' : 'Mine'}
        </button>
      ))}
    </div>
  );
}

/** Compact "Everyone ▾ / Only me" select (Your Collections › All sounds, next to the search). */
export function ScopeSelect({ value, onChange }: { value: SoundsScope; onChange: (s: SoundsScope) => void }) {
  return (
    <select
      aria-label="Whose sounds"
      value={value}
      onChange={(e) => onChange(e.target.value === 'mine' ? 'mine' : 'team')}
      className="h-9 shrink-0 rounded-[8px] border border-[#eaecf0] bg-white px-2.5 text-[13px] text-[#344054]"
    >
      <option value="team">Everyone</option>
      <option value="mine">Only me</option>
    </select>
  );
}
